import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Save, Edit3, Camera, X } from 'lucide-react';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import ImageBrowser from '../../shared/ImageBrowser';
import ModalShell from '../../shared/ModalShell';
import StickySaveBar from '../../shared/product/StickySaveBar';
import { useUpdateVendorProfile } from '../../../services/vendorProfileService';
import { useToastContext } from '../../../contexts/ToastContext';
import { useVendorSubscriptionStatus } from '../../../services/subscriptionService';

const NON_PORTFOLIO_IMAGE_QUERY = { non_portfolio: true };

const ProfileInformationTab = ({ user, updateUser, readOnly = false }) => {
 const toast = useToastContext();

 const { data: statusPayload, isLoading: subscriptionLoading } = useVendorSubscriptionStatus({
 enabled: user?.user_type === 'vendor',
 });

 const subscriptionReady = !subscriptionLoading && statusPayload?.success === true;
 const hasActiveSubscription = statusPayload?.data?.has_active_subscription === true;
 const maxGeneralImages = statusPayload?.data?.current_subscription?.limits?.max_images;

 let maxImagesForBrowser = null;
 if (user?.user_type === 'vendor' && subscriptionReady) {
 if (!hasActiveSubscription) {
 maxImagesForBrowser = 0;
 } else if (maxGeneralImages != null) {
 maxImagesForBrowser = maxGeneralImages;
 } else {
 maxImagesForBrowser = null;
 }
 }
 const [isEditing, setIsEditing] = useState(false);
 const [showImageBrowserModal, setShowImageBrowserModal] = useState(false);
 
 const [profileData, setProfileData] = useState({
 name: '',
 email: '',
 phone: '',
 address: '',
 city: '',
 state: '',
 zip_code: '',
 country: 'US',
 image_url: ''
 });
 
 const [originalProfileData, setOriginalProfileData] = useState(null);
 const [errors, setErrors] = useState({});

 useEffect(() => {
 if (user) {
 const initialData = {
 name: user.name || '',
 email: user.email || '',
 phone: user.phone || '',
 address: user.vendor_profile?.address || '',
 city: user.vendor_profile?.city || '',
 state: user.vendor_profile?.state || '',
 zip_code: user.vendor_profile?.zip_code || '',
 country: user.vendor_profile?.country || 'US',
 image_url: user.vendor_profile?.image_url || user.profile_image_url || ''
 };
 // Only update if not editing to avoid overwriting user input
 if (!isEditing) {
 setProfileData(initialData);
 setOriginalProfileData(initialData);
 }
 }
 }, [user, isEditing]);

 const handleProfileInputChange = (field, value) => {
 setProfileData(prev => ({
 ...prev,
 [field]: value
 }));
 
 if (errors[field]) {
 setErrors(prev => ({
 ...prev,
 [field]: null
 }));
 }
 };

 const validateProfileForm = () => {
 const newErrors = {};
 
 if (!profileData.name.trim()) newErrors.name = 'Name is required';
 if (!profileData.email.trim()) newErrors.email = 'Email is required';
 if (profileData.email && !/\S+@\S+\.\S+/.test(profileData.email)) {
 newErrors.email = 'Email is invalid';
 }
 if (!profileData.phone.trim()) newErrors.phone = 'Phone is required';
 
 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const updateProfileMutation = useUpdateVendorProfile({
 onSuccess: (result) => {
 // Update local state immediately with the new data
 const updatedProfileData = { ...profileData };
 if (result.data?.image_url) {
 updatedProfileData.image_url = result.data.image_url;
 }
 setProfileData(updatedProfileData);
 setOriginalProfileData(updatedProfileData);
 
 // Update user context with merged data
 if (result.data || result.user) {
 const updatedUser = { 
 ...user,
 ...(result.user || {}),
 vendor_profile: {
 ...(user?.vendor_profile || {}),
 ...(result.data || {})
 }
 };
 updateUser(updatedUser);
 }
 
 setIsEditing(false);
 toast.success('Profile Updated', 'Your profile information has been updated successfully!');
 },
 onError: (error) => {
 const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update profile';
 const errorDetails = error?.response?.data?.errors;
 
 // Handle validation errors
 if (errorDetails) {
 const fieldErrors = {};
 Object.keys(errorDetails).forEach(key => {
 fieldErrors[key] = errorDetails[key][0];
 });
 setErrors(fieldErrors);
 toast.error('Validation Error', Object.values(fieldErrors)[0] || errorMessage);
 } else {
 toast.error('Update Failed', errorMessage);
 }
 console.error('Update failed:', errorMessage);
 }
 });

 const handleUpdateProfile = () => {
 if (!validateProfileForm()) return;
 updateProfileMutation.mutate(profileData);
 };
 
 const handleCancelEdit = () => {
 if (originalProfileData) {
 setProfileData({ ...originalProfileData });
 }
 setIsEditing(false);
 };

 const handleProfileImageSelect = (image) => {
 if (image) {
 handleProfileInputChange('image_url', image.image_url);
 setShowImageBrowserModal(false);
 }
 };
 
 const hasUnsavedChanges = () => {
 if (!originalProfileData) return false;
 return JSON.stringify(profileData) !== JSON.stringify(originalProfileData);
 };

 const dirty = isEditing && hasUnsavedChanges();

 return (
 <div className={`space-y-4 sm:space-y-6 ${dirty ? 'pb-20' : ''}`}>
 <StickySaveBar
 open={dirty}
 message="Unsaved profile changes"
 onDiscard={handleCancelEdit}
 onSave={handleUpdateProfile}
 saving={updateProfileMutation.isPending}
 saveLabel="Save changes"
 />

 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
 <h3 className="text-base sm:text-lg font-medium text-gray-900">Profile Information</h3>
 {!readOnly && (
 !isEditing ? (
 <Button
 onClick={() => setIsEditing(true)}
 variant="outline"
 size="sm"
 className="flex items-center w-full sm:w-auto justify-center"
 >
 <Edit3 className="w-4 h-4 mr-2" />
 Edit Profile
 </Button>
 ) : (
 <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
 <Button
 onClick={handleCancelEdit}
 variant="outline"
 size="sm"
 className="w-full sm:w-auto"
 >
 Cancel
 </Button>
 <Button
 onClick={handleUpdateProfile}
 disabled={updateProfileMutation.isPending}
 size="sm"
 className="flex items-center justify-center w-full sm:w-auto"
 >
 {updateProfileMutation.isPending ? (
 <>
 <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
 Updating...
 </>
 ) : (
 <>
 <Save className="w-4 h-4 mr-2" />
 Save Changes
 </>
 )}
 </Button>
 </div>
 )
 )}
 </div>

 {/* Profile Image Section */}
 <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pb-4 sm:pb-6 border-b border-gray-200">
 <div className="relative flex-shrink-0">
 {profileData.image_url || user?.vendor_profile?.image_url || user?.profile_image_url ? (
 <img
 src={profileData.image_url || user.vendor_profile?.image_url || user.profile_image_url}
 alt="Profile"
 className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-gray-200"
 />
 ) : (
 <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
 <User className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
 </div>
 )}
 </div>
 <div className="flex-1 min-w-0">
 <h4 className="text-sm font-medium text-gray-900 mb-1">Profile photo</h4>
 <p className="text-xs text-gray-500 mb-3">
 Your personal account photo. Upload your business logo separately under the Business Profile tab
 (used on receipts and branding).
 </p>
 {!readOnly && (
 <Button
 onClick={() => setShowImageBrowserModal(true)}
 variant="outline"
 size="sm"
 className="flex items-center w-full sm:w-auto justify-center"
 disabled={!isEditing}
 >
 <Camera className="w-4 h-4 mr-2" />
 {profileData.image_url || user?.vendor_profile?.image_url ? 'Change Image' : 'Add Image'}
 </Button>
 )}
 </div>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
 {readOnly ? (
 <>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
 <p className="text-sm text-gray-900">{profileData.name || 'N/A'}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
 <p className="text-sm text-gray-900">{profileData.email || 'N/A'}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
 <p className="text-sm text-gray-900">{profileData.phone || 'N/A'}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
 <p className="text-sm text-gray-900">{profileData.address || 'N/A'}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
 <p className="text-sm text-gray-900">{profileData.city || 'N/A'}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
 <p className="text-sm text-gray-900">{profileData.state || 'N/A'}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
 <p className="text-sm text-gray-900">{profileData.zip_code || 'N/A'}</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
 <p className="text-sm text-gray-900">{profileData.country || 'N/A'}</p>
 </div>
 </>
 ) : (
 <>
 <Input
 label="Full Name"
 value={profileData.name}
 onChange={(e) => handleProfileInputChange('name', e.target.value)}
 error={errors.name}
 disabled={!isEditing}
 required
 />

 <Input
 label="Email Address"
 type="email"
 value={profileData.email}
 onChange={(e) => handleProfileInputChange('email', e.target.value)}
 error={errors.email}
 disabled={!isEditing}
 required
 />

 <Input
 label="Phone Number"
 value={profileData.phone}
 onChange={(e) => handleProfileInputChange('phone', e.target.value)}
 error={errors.phone}
 disabled={!isEditing}
 required
 />

 <Input
 label="Address"
 value={profileData.address}
 onChange={(e) => handleProfileInputChange('address', e.target.value)}
 error={errors.address}
 disabled={!isEditing}
 />

 <Input
 label="City"
 value={profileData.city}
 onChange={(e) => handleProfileInputChange('city', e.target.value)}
 error={errors.city}
 disabled={!isEditing}
 />

 <Input
 label="State"
 value={profileData.state}
 onChange={(e) => handleProfileInputChange('state', e.target.value)}
 error={errors.state}
 disabled={!isEditing}
 />

 <Input
 label="ZIP Code"
 value={profileData.zip_code}
 onChange={(e) => handleProfileInputChange('zip_code', e.target.value)}
 error={errors.zip_code}
 disabled={!isEditing}
 />

 <Input
 label="Country"
 value={profileData.country}
 onChange={(e) => handleProfileInputChange('country', e.target.value)}
 error={errors.country}
 disabled={!isEditing}
 />
 </>
 )}
 </div>

 <ModalShell
 isOpen={showImageBrowserModal}
 onClose={() => setShowImageBrowserModal(false)}
 labelledBy="select-profile-image-title"
 panelClassName="max-w-4xl"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-border max-h-[90vh] overflow-y-auto">
 <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border sticky top-0 bg-surface z-10">
 <h3 id="select-profile-image-title" className="text-base sm:text-lg font-medium text-ink">
 Select Profile Image
 </h3>
 <button
 type="button"
 onClick={() => setShowImageBrowserModal(false)}
 className="text-gray-400 hover:text-gray-600 transition-colors"
 aria-label="Close"
 >
 <X className="w-5 h-5" />
 </button>
 </div>

 <div className="p-4 sm:p-6">
 <p className="text-xs text-ink-tertiary mb-3">
 Shows <strong className="text-ink-secondary">general</strong> uploads only (portfolio
 images are on the Portfolio page). New uploads are saved as general images and count toward your
 plan&apos;s general image limit.
 {user?.user_type === 'vendor' && subscriptionReady && (
 <>
 {' '}
 {maxGeneralImages == null
 ? 'Current plan: unlimited general images.'
 : `Current plan: up to ${maxGeneralImages} general images.`}
 {!hasActiveSubscription && (
 <>
 {' '}
 <Link
 to="/vendor/subscription"
 className="text-ink font-medium underline"
 >
 View plans
 </Link>
 </>
 )}
 </>
 )}
 </p>
 <ImageBrowser
 listQueryParams={NON_PORTFOLIO_IMAGE_QUERY}
 uploadImageType="general"
 showUpload
 showDelete
 showSetDefault={false}
 maxImages={user?.user_type === 'vendor' ? maxImagesForBrowser : null}
 onSelectImage={handleProfileImageSelect}
 />
 </div>
 </div>
 </ModalShell>
 </div>
 );
};

export default ProfileInformationTab;

